import { EyeIcon, EyeSlashIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { InputType } from 'config'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Modal } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { defaultFooterConfigInputs } from './constants'

export const FooterConfiguration = ({
  isUserConfig = false,
  defaultData = {},
  onOk,
  onClose,
}: {
  isUserConfig?: boolean
  defaultData?: Record<string, any>
  onOk?: Function
  onClose?: Function
}) => {
  const [data, setData] = useState<Record<string, any>>({})
  const [isLoading, setLoading] = useState(false)
  const [changed, setChanged] = useState(false)

  const inputs = defaultFooterConfigInputs()

  useEffect(() => {
    if (isUserConfig) setData(defaultData || {})
    else {
      setLoading(true)
      getAdminConfig('footer')
        .then((value) => {
          setData(value || {})
        })
        .finally(() => setLoading(false))
    }
  }, [])

  const onChange = (key: string, value: any, inputType: string) => {
    const temp = cloneDeep(data)
    if (['togglebutton', 'section'].includes(inputType)) temp[key] = value
    else {
      if (!temp[key]) temp[key] = {}
      temp[key].value = value
    }
    setData(temp)
    setChanged(true)
  }

  const onChangeLink = (key: string, value: any, index: number, sectionKey: string) => {
    const temp = cloneDeep(data)
    if (!temp[sectionKey]) return
    temp[sectionKey][index][key] = value
    setData(temp)
    setChanged(true)
  }

  const onVisible = (key: string, value: boolean, index?: number) => {
    const temp = cloneDeep(data)
    if (['links', 'lenderLinks'].includes(key)) {
      temp[key][index!].visible = value
    } else {
      if (!temp[key]) temp[key] = {}
      temp[key].visible = value
    }
    setData(temp)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    await setAdminConfig('footer', data)
    setLoading(false)
    setChanged(false)
  }

  const addLink = (sectionKey: string) => {
    const temp = cloneDeep(data)
    if (!temp[sectionKey]) temp[sectionKey] = []
    temp[sectionKey].push({ label: `Link ${temp[sectionKey].length + 1}`, url: '', visible: true })
    setData(temp)
    setChanged(true)
  }

  const removeLink = (index: number, sectionKey: string) => {
    const temp = cloneDeep(data)
    if (temp[sectionKey]) temp[sectionKey].splice(index, 1)
    setData(temp)
    setChanged(true)
  }

  const renderLinkSection = (sectionKey: string) => {
    return (
      <>
        {data[sectionKey]?.map((link: Record<string, any>, index: number) => {
          const input: InputType = {
            inputType: 'text',
            title: ``,
            value: link.url,
          }

          input.additionalElements = (
            <>
              <input
                className="border-gray-300 border-[1px] px-1"
                value={link.label}
                onChange={(e: any) => onChangeLink('label', e.target.value, index, sectionKey)}
              />
              <span className="cursor-pointer" onClick={() => onVisible(sectionKey, !link.visible, index)}>
                {!!link.visible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
              </span>
              <span className="cursor-pointer text-red-800" onClick={() => removeLink(index, sectionKey)}>
                <TrashIcon className="w-4 h-4" />
              </span>
            </>
          )

          return (
            <div
              className={`col-span-${input.span || 1} ${link.visible ? 'opacity-100' : 'opacity-30 z-10'}`}
              key={`${sectionKey}-${index}`}
            >
              <RenderInput
                input={input}
                Key={`${sectionKey}-${index}`}
                key={`${sectionKey}-${index}`}
                onChange={(key: string, value: any) => onChangeLink('url', value, index, sectionKey)}
              />
            </div>
          )
        })}
        <div className="col-span-full">
          <Button link onClick={() => addLink(sectionKey)}>
            + Add Link
          </Button>
        </div>
      </>
    )
  }

  const rtl = (
    <div className="relative">
      <LayoutLoading show={isLoading} />

      <div className="grid grid-cols-1 md:grid-cols-2 sm:gird-cols-2 gap-3">
        {Object.keys(inputs).map((key) => {
          if (!isUserConfig && key === 'lenderFooterSection') return
          if (isUserConfig && key === 'section6') return
          const input: InputType = inputs[key]
          input.value = data[key]
          let visible = true
          if (!['togglebutton', 'section'].includes(input.inputType)) {
            input.value = data[key]?.value
            visible = !!data[key]?.visible
            input.additionalElements = (
              <span className="cursor-pointer" onClick={() => onVisible(key, !visible)}>
                {visible ? <EyeIcon className="w-4 h-4" /> : <EyeSlashIcon className="w-4 h-4" />}
              </span>
            )
          }

          return (
            <>
              <div className={`col-span-${input.span || 1} ${visible ? 'opacity-100' : 'opacity-30 z-10'}`} key={key}>
                <RenderInput
                  input={input}
                  Key={key}
                  key={key}
                  onChange={(key: string, value: any) => onChange(key, value, input.inputType)}
                />
              </div>
              {key === 'aboutUsDescription' && renderLinkSection('links')}
              {key === 'copyrightText' && isUserConfig && renderLinkSection('lenderLinks')}
            </>
          )
        })}
      </div>
      {!isUserConfig && <SaveChanges show={changed} label="Save Changes" onSave={onSubmit} />}
    </div>
  )

  if (isUserConfig) {
    return (
      <Modal
        isOpen
        title="Custom Footer"
        onOk={() => {
          onOk && onOk(data)
          onClose && onClose()
        }}
        onClose={() => onClose && onClose()}
      >
        {rtl}
      </Modal>
    )
  }
  return rtl
}
